/* eslint-disable @typescript-eslint/no-explicit-any */
import type { SimpleObserver } from "@tedivo/tedivo-pure-helpers";

export interface IBaseHttpClient {
  readonly baseUrl: string;
  request<TResponse>(props: IRequestProps): Promise<IResponseModel<TResponse>>;
}

export abstract class BaseHttpClient {
  public readonly baseUrl: string;

  private onOffLineObserver: SimpleObserver<{ isOnline: boolean }> | undefined =
    undefined;
  private setAuthorizationHeaders: (() => Record<string, string>) | undefined =
    undefined;
  private notifyXhrRequestFired: ((isAnonymous: boolean) => void) | undefined =
    undefined;
  private notifyError:
    | ((
        httpCode: number,
        code: "response" | "request" | "unknown",
        message: string,
      ) => void)
    | undefined = undefined;

  private baseHeaders: Record<string, string> = {};
  private readonly timeout: number;

  constructor(
    baseUrl: string,
    callbacks: IBaseHttpClientCallbacks,
    baseHeaders: Record<string, string> = {},
    timeout = 30000,
  ) {
    this.baseUrl = baseUrl.replace(/$^\//, "");

    this.onOffLineObserver = callbacks.onOffLineObserver;
    this.setAuthorizationHeaders = callbacks.setAuthorizationHeaders;
    this.notifyXhrRequestFired = callbacks.notifyXhrRequestFired;
    this.notifyError = callbacks.notifyError;
    this.baseHeaders = baseHeaders;
    this.timeout = timeout;
  }

  public async request<TResponse>({
    method,
    url,
    data,
    isAnonymous = false,
  }: IRequestProps): Promise<IResponseModel<TResponse>> {
    if (this.onOffLineObserver?.getValue("isOnline") === false) {
      this.onOffLineObserver.notify();
      return {
        statusCode: 503,
        code: "general:onOffLine.offline",
        message: "The application is offline",
        data: undefined as never,
      };
    }

    let headers: Record<string, string> = { ...this.baseHeaders };

    if (this.setAuthorizationHeaders && !isAnonymous) {
      headers = { ...headers, ...this.setAuthorizationHeaders() };
    }

    if (!isAnonymous && this.notifyXhrRequestFired)
      this.notifyXhrRequestFired(isAnonymous);

    const callUrl = `${this.baseUrl}/${url
      .replace(/\/{2,4}/g, "/")
      .replace(/^\//, "")}`;

    const init: RequestInit = {
      method,
      headers,
      signal: AbortSignal.timeout(this.timeout),
    };

    let fetchUrl = callUrl;

    switch (method) {
      case "GET":
        fetchUrl = appendQueryParams(callUrl, data ? { id: data } : undefined);
        break;
      case "DELETE":
        fetchUrl = appendQueryParams(
          callUrl,
          data as Record<string, unknown> | undefined,
        );
        break;
      case "POST":
      case "PUT":
        if (data !== undefined) {
          if (data instanceof FormData) {
            init.body = data;
          } else {
            init.body = JSON.stringify(data);
            init.headers = { ...headers, "Content-Type": "application/json" };
          }
        }
        break;
    }

    let response: Response;

    try {
      response = await fetch(fetchUrl, init);
    } catch (error: unknown) {
      // The request was made but no response was received (network failure, timeout/abort, etc.)
      console.log("Error in network request", error);
      console.log("Error request");
      this.notifyError?.(
        400,
        "request",
        "The request was made but no response was received",
      );

      return {
        statusCode: 400,
        code: "",
        message: "Unknown error",
        data: undefined as never,
      };
    }

    const body = await parseResponseBody(response);

    if (!response.ok) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      const httpCode = response.status || 400;
      const errRes = body as IResponseErrorData | string | undefined;

      this.notifyError?.(
        httpCode,
        "response",
        httpCode === 401 || httpCode === 403
          ? (typeof errRes === "object" && errRes?.message) ||
              "errors:notAllowed"
          : typeof errRes === "string"
          ? errRes
          : (errRes as any)?.translationKey ||
            (errRes as any)?.message ||
            "errors:errorHasOcurred",
      );

      return {
        statusCode: httpCode,
        code: typeof errRes === "object" ? errRes?.code : undefined,
        message:
          (typeof errRes === "object" &&
            (errRes?.message || errRes?.translationKey)) ||
          "Unknown error",
        data: undefined as never,
      };
    }

    return {
      statusCode: response.status,
      data: body as TResponse,
    };
  }
}

function appendQueryParams(
  url: string,
  params: Record<string, unknown> | undefined,
): string {
  if (!params) return url;

  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) search.append(key, String(value));
  });

  const qs = search.toString();
  if (!qs) return url;

  return `${url}${url.includes("?") ? "&" : "?"}${qs}`;
}

async function parseResponseBody(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 205) return undefined;

  const text = await response.text();
  if (!text) return undefined;

  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) return text;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

interface IRequestProps<T = unknown> {
  method: "GET" | "POST" | "DELETE" | "PUT";
  url: string;
  data?: TextDecodeOptions | FormData | T;
  isAnonymous?: boolean;
}

export type IResponseModel<T> =
  | {
      statusCode: number;
      data?: T;
      code?: never;
      message?: never;
    }
  | {
      statusCode: number;
      data: never;
      code?: string;
      message?: string;
    };

export type IResponseModelWithPagination<T> =
  | {
      statusCode: number;
      data?: TDataWithPagination<T>;
      code?: never;
      message?: never;
    }
  | {
      statusCode: number;
      data: never;
      code?: string;
      message?: string;
    };

export type TDataWithPagination<T> = {
  lastEvaluatedKey: string;
  data: T;
};

export type IResponseErrorData = {
  code?: string;
  message?: string;
  translationKey?: string;
};

export interface IBaseHttpClientCallbacks {
  onOffLineObserver?: SimpleObserver<{ isOnline: boolean }>;
  setAuthorizationHeaders?: () => Record<string, string>;
  notifyXhrRequestFired?: (isAnonymous: boolean) => void;
  notifyError?: (
    httpCode: number,
    code: "response" | "request" | "unknown",
    message: string,
  ) => void;
}

export function isHttpStatusCodeSuccess(statusCode: number): boolean {
  return statusCode >= 200 && statusCode < 300;
}
